import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ClipboardList, CheckCircle, XCircle, Clock, Plus, Filter, Eye, Loader2, User, Calendar, Activity, TrendingUp } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { useProject } from '../contexts/ProjectContext';
import api from '../lib/api';
import { format } from 'date-fns';

const FieldUpdatesPage = () => {
  const { activeSchedule, activeProject } = useProject();
  const [fieldUpdates, setFieldUpdates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUpdate, setSelectedUpdate] = useState(null);
  const [stats, setStats] = useState({
    pending: 0,
    approved: 0,
    total: 0
  });

  // Fetch field updates - activities that have been updated (same source as Dashboard)
  const fetchFieldUpdates = useCallback(async () => {
    if (!activeSchedule?.id) return;
    
    setLoading(true);
    try {
      // Fetch all activities for the schedule - same data source as Dashboard
      const response = await api.get(`/activities/schedule/${activeSchedule.id}`);
      
      // Dashboard counts ALL activities with updated_at in current month
      // So we show all activities that have been updated (have updated_at field)
      // This matches the gamification metrics query exactly
      const allActivities = response.data;
      
      // Transform to field update format - include ALL activities
      // Dashboard counts: activities with updated_at >= month_start
      const updates = allActivities.map(activity => ({
        id: activity.id,
        activity_id: activity.activity_id,
        activity_name: activity.name,
        wbs_id: activity.wbs_id,
        wbs_code: activity.wbs_code,
        wbs_name: activity.wbs_name,
        // Use updated_at as timestamp
        timestamp: activity.updated_at || activity.created_at,
        // Progress data
        percent_complete: activity.percent_complete || 0,
        actual_start: activity.actual_start,
        actual_finish: activity.actual_finish,
        status: activity.status || 'not_started',
        original_duration: activity.original_duration,
        remaining_duration: activity.remaining_duration,
        // Derive update type
        update_type: deriveUpdateType(activity),
        // All applied updates are considered approved
        approval_status: 'approved'
      }));
      
      // Sort by updated_at descending (most recent first)
      updates.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      
      setFieldUpdates(updates);
      
      // Calculate stats based on status
      const inProgress = updates.filter(u => u.status === 'in_progress').length;
      const completed = updates.filter(u => u.status === 'completed').length;
      
      setStats({
        pending: 0, // No pending workflow yet
        approved: updates.length,
        total: updates.length
      });
    } catch (error) {
      console.error('Failed to fetch field updates:', error);
    } finally {
      setLoading(false);
    }
  }, [activeSchedule?.id]);

  // Derive update type from activity data
  const deriveUpdateType = (activity) => {
    if (activity.status === 'completed' || activity.actual_finish) {
      return 'completed';
    }
    if (activity.status === 'in_progress' || activity.actual_start) {
      return 'in_progress';
    }
    if (activity.percent_complete > 0) {
      return 'progress';
    }
    return 'update';
  };

  useEffect(() => {
    fetchFieldUpdates();
  }, [fetchFieldUpdates]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'in_progress':
        return <TrendingUp className="h-4 w-4 text-blue-500" />;
      default:
        return <Clock className="h-4 w-4 text-amber-500" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-100 text-green-700 text-[10px]">Completed</Badge>;
      case 'in_progress':
        return <Badge className="bg-blue-100 text-blue-700 text-[10px]">In Progress</Badge>;
      case 'not_started':
        return <Badge className="bg-slate-100 text-slate-600 text-[10px]">Not Started</Badge>;
      default:
        return <Badge className="bg-slate-100 text-slate-600 text-[10px]">{status}</Badge>;
    }
  };

  const getUpdateTypeBadge = (type) => {
    switch (type) {
      case 'completed':
        return <Badge variant="outline" className="text-[10px] text-emerald-600 border-emerald-200">Completed</Badge>;
      case 'in_progress':
        return <Badge variant="outline" className="text-[10px] text-blue-600 border-blue-200">Started</Badge>;
      case 'progress':
        return <Badge variant="outline" className="text-[10px] text-purple-600 border-purple-200">Progress</Badge>;
      default:
        return <Badge variant="outline" className="text-[10px]">Update</Badge>;
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '—';
    try {
      return format(new Date(timestamp), 'MMM d, yyyy h:mm a');
    } catch {
      return String(timestamp);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return format(new Date(dateStr), 'MMM d, yyyy');
    } catch {
      return String(dateStr);
    }
  };

  return (
    <div className="space-y-6" data-testid="field-updates-page">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
            Schedule Updates
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Activity progress and status updates from the field
            {activeSchedule && <span className="text-slate-400"> • {activeSchedule.name}</span>}
          </p>
        </div>
        
        <Button 
          className="h-9 bg-slate-900 text-white hover:bg-slate-800 font-medium gap-2"
          disabled
          data-testid="submit-update-button"
        >
          <Plus className="h-4 w-4" />
          Submit Update
        </Button>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">In Progress</p>
                <p className="text-xl font-semibold text-slate-900 tabular-nums">
                  {fieldUpdates.filter(u => u.status === 'in_progress').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Completed</p>
                <p className="text-xl font-semibold text-slate-900 tabular-nums">
                  {fieldUpdates.filter(u => u.status === 'completed').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center">
                <ClipboardList className="h-5 w-5 text-slate-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Total Updates</p>
                <p className="text-xl font-semibold text-slate-900 tabular-nums">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Update Inbox - 2 columns */}
        <Card className="border-slate-200 lg:col-span-2">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold text-slate-900">
                Update Inbox
              </CardTitle>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-[10px]">
                  {fieldUpdates.length} updates
                </Badge>
                <Button variant="outline" size="sm" className="h-8 gap-2 text-xs" disabled>
                  <Filter className="h-3 w-3" />
                  Filter
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
              </div>
            ) : fieldUpdates.length > 0 ? (
              <div className="space-y-3 max-h-[600px] overflow-y-auto">
                {fieldUpdates.map((update) => (
                  <div 
                    key={update.id} 
                    className={`flex items-start gap-4 p-4 rounded-lg border transition-colors cursor-pointer ${
                      selectedUpdate?.id === update.id 
                        ? 'border-blue-300 bg-blue-50/50' 
                        : 'border-slate-100 bg-slate-50/50 hover:border-slate-200 hover:bg-slate-50'
                    }`}
                    onClick={() => setSelectedUpdate(update)}
                  >
                    <div className="flex-shrink-0 pt-1">
                      {getStatusIcon(update.status)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap min-w-0">
                        <span className="font-medium text-sm text-slate-900 truncate min-w-0">
                          {update.activity_name}
                        </span>
                        {getStatusBadge(update.status)}
                        {getUpdateTypeBadge(update.update_type)}
                      </div>
                      
                      {/* Activity ID */}
                      <p className="text-xs text-slate-500 mb-1 font-mono">
                        {update.activity_id}
                      </p>
                      
                      {/* Progress details */}
                      <div className="flex flex-wrap sm:flex-nowrap items-center gap-4 text-xs text-slate-600 mb-2">
                        {update.percent_complete > 0 && (
                          <span className="flex items-center gap-1">
                            <div className="w-16 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                              <div 
                                className={`h-full rounded-full ${update.percent_complete >= 100 ? 'bg-green-500' : 'bg-blue-500'}`}
                                style={{ width: `${Math.min(100, update.percent_complete)}%` }}
                              />
                            </div>
                            <span className="font-medium">{Math.round(update.percent_complete)}%</span>
                          </span>
                        )}
                        {update.actual_start && (
                          <span>Start: {formatDate(update.actual_start)}</span>
                        )}
                        {update.actual_finish && (
                          <span>Finish: {formatDate(update.actual_finish)}</span>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-3 text-xs text-slate-400">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          Updated {formatTimestamp(update.timestamp)}
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 gap-1 shrink-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedUpdate(update);
                      }}
                    >
                      <Eye className="h-3 w-3" />
                      View
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              /* Empty state */
              <div className="flex flex-col items-center justify-center py-12">
                <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-3">
                  <ClipboardList className="h-6 w-6 text-slate-300" />
                </div>
                <p className="text-sm font-medium text-slate-900 mb-1">No field updates yet</p>
                <p className="text-xs text-slate-400 text-center max-w-xs mb-3">
                  {activeSchedule 
                    ? 'Activities with progress updates will appear here'
                    : 'Select a project and schedule to view field updates'}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Details Panel */}
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold text-slate-900">
              {selectedUpdate ? 'Update Details' : 'Select an Update'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {selectedUpdate ? (
              <>
                {/* Activity Info */}
                <div className="p-3 rounded-lg bg-slate-50 border border-slate-100">
                  <p className="text-xs text-slate-500 mb-1">Activity</p>
                  <div className="flex items-center gap-2">
                    <Activity className="h-4 w-4 text-slate-400" />
                    <span className="text-sm font-medium text-slate-900">{selectedUpdate.activity_name}</span>
                  </div>
                  <p className="text-xs text-slate-400 font-mono mt-1">{selectedUpdate.activity_id}</p>
                </div>
                
                {/* Status */}
                <div className="p-3 rounded-lg bg-slate-50 border border-slate-100">
                  <p className="text-xs text-slate-500 mb-2">Status</p>
                  {getStatusBadge(selectedUpdate.status)}
                </div>
                
                {/* Progress */}
                <div className="p-3 rounded-lg bg-blue-50 border border-blue-100">
                  <p className="text-xs text-blue-600 mb-2">Progress</p>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-2 bg-blue-200 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full ${selectedUpdate.percent_complete >= 100 ? 'bg-green-500' : 'bg-blue-500'}`}
                        style={{ width: `${Math.min(100, selectedUpdate.percent_complete)}%` }}
                      />
                    </div>
                    <span className="text-sm font-semibold text-blue-700">
                      {Math.round(selectedUpdate.percent_complete)}%
                    </span>
                  </div>
                </div>
                
                {/* Dates */}
                <div className="space-y-2">
                  {selectedUpdate.actual_start && (
                    <div className="p-3 rounded-lg bg-purple-50 border border-purple-100">
                      <p className="text-xs text-purple-600 mb-1">Actual Start</p>
                      <p className="text-sm font-medium text-purple-900">
                        {formatDate(selectedUpdate.actual_start)}
                      </p>
                    </div>
                  )}
                  
                  {selectedUpdate.actual_finish && (
                    <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-100">
                      <p className="text-xs text-emerald-600 mb-1">Actual Finish</p>
                      <p className="text-sm font-medium text-emerald-900">
                        {formatDate(selectedUpdate.actual_finish)}
                      </p>
                    </div>
                  )}
                </div>
                
                {/* Duration Info */}
                <div className="p-3 rounded-lg bg-slate-50 border border-slate-100">
                  <p className="text-xs text-slate-500 mb-2">Duration</p>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">Original</span>
                    <span className="font-medium">{selectedUpdate.original_duration}d</span>
                  </div>
                  <div className="flex items-center justify-between text-sm mt-1">
                    <span className="text-slate-600">Remaining</span>
                    <span className="font-medium">{selectedUpdate.remaining_duration}d</span>
                  </div>
                </div>
                
                {/* Last Updated */}
                <div className="p-3 rounded-lg bg-slate-50 border border-slate-100">
                  <p className="text-xs text-slate-500 mb-1">Last Updated</p>
                  <p className="text-sm text-slate-700">{formatTimestamp(selectedUpdate.timestamp)}</p>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-slate-500">
                  Select an update from the inbox to view details
                </p>

                <Separator />

                {/* Placeholder panels */}
                <div className="space-y-3">
                  <div className="p-3 rounded-lg bg-slate-50 border border-slate-100">
                    <p className="text-xs text-slate-500 mb-2">Activity</p>
                    <div className="h-4 w-full bg-slate-200 rounded" />
                  </div>
                  
                  <div className="p-3 rounded-lg bg-slate-50 border border-slate-100">
                    <p className="text-xs text-slate-500 mb-2">Progress</p>
                    <div className="h-4 w-full bg-slate-200 rounded" />
                  </div>
                  
                  <div className="p-3 rounded-lg bg-slate-50 border border-slate-100">
                    <p className="text-xs text-slate-500 mb-2">Dates</p>
                    <div className="h-8 w-full bg-slate-200 rounded" />
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default FieldUpdatesPage;
